import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';
import { GUIDE_SECTIONS } from '../../content/guide/sections';
import type { GuideArticle, GuideBlock, GuideIconName } from '../../content/guide/types';

function getIcon(name: GuideIconName) {
  switch (name) {
    case 'Pulse':      return Icons.Pulse;
    case 'Book':       return Icons.Book;
    case 'Calendar':   return Icons.Calendar;
    case 'Storefront': return Icons.Storefront;
    case 'Heart':      return Icons.Heart;
    case 'Clipboard':  return Icons.Clipboard;
  }
}

function ArticleBody({ body }: { body: readonly GuideBlock[] }) {
  return (
    <div className="guide-article-body">
      {body.map((block, i) => {
        switch (block.type) {
          case 'h':
            return <h3 key={i} className="guide-h">{block.text}</h3>;
          case 'p':
            return <p key={i} className="guide-p">{block.text}</p>;
          case 'list':
            return <ul key={i} className="guide-list">{block.items.map((it, j) => <li key={j}>{it}</li>)}</ul>;
          case 'ol':
            return <ol key={i} className="guide-ol">{block.items.map((it, j) => <li key={j}>{it}</li>)}</ol>;
          case 'dl':
            return (
              <dl key={i} className="guide-dl">
                {block.items.map(([k, v], j) => (
                  <div key={j} className="guide-dl-row">
                    <dt>{k}</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            );
          case 'tip':
            return (
              <div key={i} className="guide-tip">
                <Icons.AlertTriangle size={14} />
                <span>{block.text}</span>
              </div>
            );
          case 'table':
            return (
              <div key={i} className="guide-table-wrap">
                <table className="table">
                  <thead><tr>{block.headers.map((h, j) => <th key={j}>{h}</th>)}</tr></thead>
                  <tbody>
                    {block.rows.map((row, j) => (
                      <tr key={j}>{row.map((cell, k) => <td key={k}>{cell}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          case 'statelist':
            return (
              <div key={i} className="guide-statelist">
                {block.states.map((s, j) => (
                  <div key={j} className="guide-state-row">
                    <span className="pill" data-tone={s.tone} style={{ minWidth: 200, justifyContent: 'flex-start' }}>{s.code}</span>
                    <span className="guide-state-desc">{s.desc}</span>
                  </div>
                ))}
              </div>
            );
          case 'qa':
            return (
              <div key={i} className="guide-qa">
                {block.items.map(([q, a], j) => (
                  <details key={j} className="guide-qa-item">
                    <summary>{q}</summary>
                    <p>{a}</p>
                  </details>
                ))}
              </div>
            );
        }
      })}
    </div>
  );
}

interface SearchHit {
  article: GuideArticle;
  sectionId: string;
  sectionTitle: string;
}

export function GuidePage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Parse hash for deep-linking: #/<section>/<article>
  const initial = useMemo(() => {
    const hash = location.hash.replace(/^#\/?/, '');
    const [sId, aId] = hash.split('/');
    const section = GUIDE_SECTIONS.find((s) => s.id === sId) ?? GUIDE_SECTIONS[0];
    const article = section.articles.find((a) => a.id === aId) ?? section.articles[0];
    return { sectionId: section.id, articleId: article.id };
  }, [location.hash]);

  const [activeSection, setActiveSection] = useState(initial.sectionId);
  const [activeArticle, setActiveArticle] = useState(initial.articleId);
  const [query, setQuery] = useState('');

  const allArticles = useMemo<SearchHit[]>(() =>
    GUIDE_SECTIONS.flatMap((s) =>
      s.articles.map((a) => ({ article: a, sectionId: s.id, sectionTitle: s.title })),
    ),
  []);

  const searchResults = useMemo<SearchHit[] | null>(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    return allArticles.filter((h) =>
      h.article.title.toLowerCase().includes(q) ||
      h.article.excerpt.toLowerCase().includes(q) ||
      h.sectionTitle.toLowerCase().includes(q),
    );
  }, [query, allArticles]);

  const section = GUIDE_SECTIONS.find((s) => s.id === activeSection);
  const article = section?.articles.find((a) => a.id === activeArticle) ?? section?.articles[0];

  function openArticle(sectionId: string, articleId: string) {
    setActiveSection(sectionId);
    setActiveArticle(articleId);
    setQuery('');
    void navigate(`#/${sectionId}/${articleId}`, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function pickSection(sectionId: string) {
    const s = GUIDE_SECTIONS.find((x) => x.id === sectionId);
    if (!s || s.articles.length === 0) return;
    openArticle(sectionId, s.articles[0].id);
  }

  // Sync hash → state when user uses back/forward
  useEffect(() => {
    setActiveSection(initial.sectionId);
    setActiveArticle(initial.articleId);
  }, [initial.sectionId, initial.articleId]);

  return (
    <div>
      <PageHead
        title="Guía"
        sub="Todo lo que necesitas saber para usar la plataforma. Empieza por Primeros pasos si es tu primera vez."
        meta={<span><span className="mono">{allArticles.length}</span> artículos · {GUIDE_SECTIONS.length} secciones</span>}
      />

      <div style={{ marginBottom: 24 }}>
        <div className="search" style={{ width: '100%', maxWidth: 520 }}>
          <Icons.Search size={14} />
          <input
            className="input"
            placeholder="Buscar en la guía… (estados, reservas, horarios…)"
            value={query}
            onChange={(e) => { setQuery(e.target.value); }}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      {searchResults && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-head">
            <span className="card-title">
              {searchResults.length} {searchResults.length === 1 ? 'resultado' : 'resultados'} para “{query}”
            </span>
          </div>
          <div>
            {searchResults.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-muted)' }}>
                <h3 style={{ margin: 0 }}>Sin resultados</h3>
                <div style={{ marginTop: 6, fontSize: 13 }}>Prueba con otras palabras o navega por las secciones.</div>
              </div>
            ) : searchResults.map((h) => (
              <button
                key={`${h.sectionId}-${h.article.id}`}
                type="button"
                className="guide-search-row"
                onClick={() => { openArticle(h.sectionId, h.article.id); }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="guide-search-meta mono">{h.sectionTitle.toUpperCase()} · {h.article.time}</div>
                  <div className="guide-search-title">{h.article.title}</div>
                  <div className="guide-search-excerpt">{h.article.excerpt}</div>
                </div>
                <Icons.ChevR size={14} />
              </button>
            ))}
          </div>
        </div>
      )}

      {!searchResults && (
        <>
          <div className="guide-overview" style={{ marginBottom: 28 }}>
            {GUIDE_SECTIONS.map((s) => {
              const Icon = getIcon(s.icon);
              return (
                <button
                  key={s.id}
                  type="button"
                  className="guide-section-card"
                  data-active={activeSection === s.id || undefined}
                  onClick={() => { pickSection(s.id); }}
                >
                  <div className="guide-section-icon"><Icon size={18} /></div>
                  <div className="guide-section-info">
                    <div className="guide-section-title">{s.title}</div>
                    <div className="guide-section-blurb">{s.blurb}</div>
                    <div className="guide-section-count mono">{s.articles.length} artículos →</div>
                  </div>
                </button>
              );
            })}
          </div>

          {section && article && (
            <div className="guide-reader">
              <aside className="guide-sidenav">
                <div className="guide-sidenav-eyebrow mono">{section.title}</div>
                <nav>
                  {section.articles.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      className="guide-sidenav-item"
                      data-active={activeArticle === a.id || undefined}
                      onClick={() => { openArticle(section.id, a.id); }}
                    >
                      <span>{a.title}</span>
                      <span className="guide-sidenav-time mono">{a.time}</span>
                    </button>
                  ))}
                </nav>
              </aside>

              <article className="guide-article">
                <div className="guide-article-head">
                  <div className="guide-breadcrumb mono">{section.title} · {article.time}</div>
                  <h1 className="guide-title">{article.title}</h1>
                  <p className="guide-excerpt">{article.excerpt}</p>
                </div>

                <ArticleBody body={article.body} />
              </article>
            </div>
          )}
        </>
      )}
    </div>
  );
}
